/**
 * AsyncCombobox — small reusable server-searched picker (Slice C0).
 *
 * The synchronous ``EntityCombobox`` expects the caller to have loaded
 * every option up front. That breaks once a domain outgrows one page:
 * the work-report form used to pull ``fetchAdminTasks({limit: 200})``
 * into a native <select>, so task 201 was silently unreachable. This
 * component instead asks the SERVER for one page at a time.
 *
 * Deliberately NOT a generic entity-picker framework: it knows nothing
 * about any domain. Callers supply a ``fetchPage`` function and a
 * ``renderItem``; a domain picker is a thin wrapper around this (see
 * ``workReports/TaskPicker.jsx``, its only consumer today).
 *
 * Contract
 * --------
 *   fetchPage({ query, offset, limit, signal }) =>
 *       Promise<{ items: object[], total: number }>
 *   getItemId(item)    -> string   (stable identity)
 *   getItemLabel(item) -> string   (single-line label for the trigger)
 *   renderItem(item)   -> ReactNode (multi-line row inside the list)
 *
 * ``value`` is the selected id ("" = none). ``onChange(id, item)`` fires
 * with the full row so the caller can keep a hydrated copy for the
 * trigger; ``onChange("", null)`` is the clear/unlink path.
 *
 * ``selectedItem`` lets a caller hydrate the CURRENT selection from a
 * detail endpoint. It is rendered on the trigger even when that row is
 * absent from page 1 of the search — an edit form must never appear to
 * drop a link just because the row sorts late.
 *
 * Robustness
 * ----------
 *   - 300 ms debounce on typing.
 *   - A monotonic request counter drops STALE responses: a slow early
 *     query must never overwrite a newer one's results.
 *   - "Mehr laden" appends by offset and de-duplicates by id, so a row
 *     shifting between pages cannot appear twice.
 *   - A changed query resets paging and discards the previous pages.
 *   - Server errors surface inline with a retry; the surrounding form
 *     stays usable.
 *
 * Theming is token-only (bg-popover / bg-background / border-input /
 * text-foreground …) so light and dark both work without a second
 * theme system.
 */
import * as React from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import useAsyncPagedSearch, {
  DEFAULT_PAGE_SIZE,
} from "./useAsyncPagedSearch";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
} from "@/components/ui/command";

// Re-exported: pre-ME3a callers import it from here.
export { DEFAULT_PAGE_SIZE };

export default function AsyncCombobox({
  label,
  placeholder = "— keine —",
  searchPlaceholder = "Suchen …",
  clearLabel = "— keine —",
  // C3: domain pickers name what is loading ("Mieter werden geladen …")
  // instead of a bare "Lädt …". Optional — the default is what every
  // pre-C3 caller already rendered, so none of them changes behaviour.
  loadingText = "Lädt …",
  emptyText = "Keine Treffer.",
  errorText = "Suche konnte nicht geladen werden.",
  value,
  selectedItem = null,
  onChange,
  fetchPage,
  getItemId,
  getItemLabel,
  renderItem,
  pageSize = DEFAULT_PAGE_SIZE,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  // ME3a: debounce, paging, stale-response and de-duplication moved to
  // ``useAsyncPagedSearch`` so the multi-select sibling shares them
  // instead of owning a second copy. Behaviour here is unchanged.
  const {
    query,
    setQuery,
    items,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    reload,
    loadMore,
  } = useAsyncPagedSearch({
    open,
    fetchPage,
    getItemId,
    pageSize,
    errorText,
  });

  const triggerId = React.useId();
  // cmdk's <Command.List> assigns its OWN dom id and drops an `id` prop,
  // so pointing aria-controls at a locally generated id left a DANGLING
  // reference (C0 defect, found in C3). Read the real id off the node
  // once the popover is mounted instead.
  const [listboxId, setListboxId] = React.useState(null);
  // A callback ref, not an effect: Radix mounts the popover content AFTER
  // `open` flips, so an effect keyed on `open` still sees a null node.
  // This fires exactly when the list attaches, and again with null when
  // it unmounts — so the attribute is never stale.
  const handleListRef = React.useCallback((node) => {
    setListboxId(node?.id || null);
  }, []);
  const handleSelect = (item) => {
    onChange(String(getItemId(item)), item);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("", null);
    setOpen(false);
  };

  const triggerText = selectedItem
    ? getItemLabel(selectedItem)
    : value
      ? // Selected but not hydrated yet — never show "nothing selected",
        // that would read as a lost link.
        "…"
      : placeholder;

  return (
    <div>
      <label
        htmlFor={triggerId}
        className="mb-1 block text-[11px] font-medium text-muted-foreground"
      >
        {label}
      </label>
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <button
            id={triggerId}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-controls={open && listboxId ? listboxId : undefined}
            disabled={disabled}
            className="flex w-full items-center justify-between rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
          >
            <span
              className={cn(
                "truncate text-left",
                !selectedItem && !value && "text-muted-foreground"
              )}
            >
              {triggerText}
            </span>
            <ChevronsUpDown
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[60] p-0"
          style={{ width: "var(--radix-popover-trigger-width)" }}
        >
          {/* shouldFilter=false: the SERVER does the filtering. Leaving
              cmdk's client filter on would hide server matches whose
              label does not literally contain the query. */}
          <Command shouldFilter={false} className="bg-transparent">
            <CommandInput
              placeholder={searchPlaceholder}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList ref={handleListRef}>
              {/* Live region: screen readers hear loading / error / count
                  without the list stealing focus. */}
              <div aria-live="polite" className="sr-only">
                {loading
                  ? loadingText
                  : error
                    ? errorText
                    : `${items.length} von ${total} Ergebnissen`}
              </div>

              {error ? (
                <div className="px-3 py-4 text-[12px]" role="alert">
                  <p className="m-0 text-destructive">{errorText}</p>
                  <button
                    type="button"
                    onClick={reload}
                    className="mt-2 rounded-[6px] border border-border px-2 py-1 text-[11px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Erneut versuchen
                  </button>
                </div>
              ) : null}

              {!error && loading ? (
                <p className="flex items-center gap-2 px-3 py-4 text-[12px] text-muted-foreground">
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin"
                    aria-hidden="true"
                  />
                  {loadingText}
                </p>
              ) : null}

              {!error && !loading && items.length === 0 ? (
                <p className="px-3 py-4 text-center text-[12px] text-muted-foreground">
                  {emptyText}
                </p>
              ) : null}

              {!error ? (
                <>
                  <CommandItem
                    value="__clear__"
                    aria-label={`${label}: Verknüpfung entfernen`}
                    onSelect={handleClear}
                    className="text-foreground data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        !value ? "opacity-100" : "opacity-0"
                      )}
                      aria-hidden="true"
                    />
                    {clearLabel}
                  </CommandItem>

                  {items.map((item) => {
                    const id = String(getItemId(item));
                    return (
                      <CommandItem
                        key={id}
                        value={id}
                        onSelect={() => handleSelect(item)}
                        className="items-start text-foreground data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
                      >
                        <Check
                          className={cn(
                            "mr-2 mt-0.5 h-4 w-4 shrink-0",
                            String(value) === id ? "opacity-100" : "opacity-0"
                          )}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1">
                          {renderItem(item)}
                        </span>
                      </CommandItem>
                    );
                  })}

                  {hasMore ? (
                    <div className="px-2 py-2">
                      <button
                        type="button"
                        onClick={loadMore}
                        disabled={loadingMore}
                        className="w-full rounded-[6px] border border-border px-2 py-1.5 text-[11px] font-medium text-foreground hover:bg-muted disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {loadingMore ? "Lädt …" : "Mehr laden"}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
